import type { DashboardMetricsResponse } from '../../../types/dashboard';
import { toDemographicCounts } from '../demographics';
import type { AtlasData } from './types';
import { MAX_QUARTERS_CHART } from '../constants';

export function latest<T>(arr: T[]): T {
  return arr[arr.length - 1];
}

export function previous<T>(arr: T[]): T | null {
  return arr.length >= 2 ? arr[arr.length - 2] : null;
}

export function pctChange(current: number, prevVal: number | null): number | null {
  if (prevVal === null || prevVal === 0) return null;
  return ((current - prevVal) / prevVal) * 100;
}

const asNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

export function mapApiToAtlasData(data: DashboardMetricsResponse | null | undefined): AtlasData | null {
  if (!data) return null;
  const section = data.sections.find((entry) => entry.sectionId === 'atlas');
  if (!section) return null;

  const byId = new Map(section.metrics.map((metric) => [metric.metricId, metric]));

  const participants = byId.get('atlas.participants');
  const completions = byId.get('atlas.completions');
  const applicants = byId.get('atlas.applicants');
  const employer = byId.get('atlas.employerSatisfaction');

  return {
    participants: (participants?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      participantsStarted: asNumber(p.participantsStarted),
      byRace: toDemographicCounts(p.byRace),
      byGender: toDemographicCounts(p.byGender),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    participantsAllTimeTotal: participants ? asNumber(participants.allTime?.participantsStarted) : 0,
    participantsAllTimeByRace: toDemographicCounts(participants?.allTime?.byRace),
    participantsAllTimeByGender: toDemographicCounts(participants?.allTime?.byGender),
    completions: (completions?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      completed: asNumber(p.completed),
      completedByRace: toDemographicCounts(p.completedByRace),
      completedByGender: toDemographicCounts(p.completedByGender),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    completionsAllTimeTotal: completions ? asNumber(completions.allTime?.completed) : 0,
    completionsAllTimeByRace: toDemographicCounts(completions?.allTime?.completedByRace),
    completionsAllTimeByGender: toDemographicCounts(completions?.allTime?.completedByGender),
    applicants: (applicants?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      applicants: asNumber(p.applicants),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
    applicantsAllTimeTotal: applicants ? asNumber(applicants.allTime?.applicants) : 0,
    employerSatisfaction: (employer?.series ?? []).slice(-MAX_QUARTERS_CHART).map((p) => ({
      quarter: String(p.quarter),
      percentSatisfied: asNumber(p.percentSatisfied),
      qoq: (p.qoq ?? {}) as Record<string, unknown>,
      yoy: (p.yoy ?? {}) as Record<string, unknown>,
    })) ?? [],
  };
}
